import joi from 'joi';
import React, { useMemo } from 'react';
import { Checkbox, FormGroup, MenuItem, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import { TransactionTableV2Attributes } from '@loyalty/editor-components';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { useTranslation } from 'react-i18next';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import { useCurrencies } from '../../../../currency/CurrenciesContext';

type FormValue = TraitFormValue<TransactionTableV2Attributes, void>;

export const transactionHistoryTableV2WidgetTraits = joi.object<TraitFormValue>({
  attributes: joi.object<TransactionTableV2Attributes>({
    currencyIds: joi.array().items(joi.string().uuid()).optional(),
  }),
});

export function AchievementsHistoryTableV2WidgetTraits() {
  const { t, i18n } = useTranslation();
  const { currencies } = useCurrencies();
  const { control } = useFormContext<FormValue>();

  const allCurrencyIds = useMemo(() => currencies.map(currency => currency.id), [currencies]);

  const currencyOptions = useMemo(
    () =>
      currencies.map((currency: CurrencyAttributes) => {
        const name = getLocalizedProperty(currency, 'name', i18n.language) || 'No name';
        return {
          id: currency.id,
          name: currency.deletedAt ? `${name} (${t('master-detail.filters.archived')})` : name,
        };
      }),
    [currencies, i18n.language, t],
  );

  return (
    <FormGroup>
      <Trait>
        <Controller
          control={control}
          name="attributes.currencyIds"
          defaultValue={allCurrencyIds}
          render={({ field: { ref, value, ...field } }) => {
            const selectedCurrencyIds = value ?? allCurrencyIds;

            return (
              <TextField
                {...field}
                inputRef={ref}
                label={t('editor.leaderboard.traits.currency')}
                select
                fullWidth
                size="small"
                value={selectedCurrencyIds}
                onChange={event => {
                  const nextValue = event.target.value;
                  field.onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
                }}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: selected =>
                    t('editor.interactions-list.traits.selectedCount', {
                      count: (selected as string[]).length,
                    }),
                }}
              >
                {currencyOptions.map(currency => (
                  <MenuItem key={currency.id} value={currency.id} dense>
                    <Checkbox
                      size="small"
                      checked={selectedCurrencyIds.includes(currency.id)}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <Typography>{currency.name}</Typography>
                  </MenuItem>
                ))}
              </TextField>
            );
          }}
        />
      </Trait>
    </FormGroup>
  );
}
